import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { actions } from '../../../../redux/reducers/prescriptionReducer';
import { getPrescriptionPerformanceByYear } from '../../../../redux/thunks/prescriptionThunks';
import { CustomButtonGroup, StackedBarChart } from '../../../shared';
import { CHART_OPTIONS } from './constants';
import PerformanceSkeleton from './skeleton';

const MonthlyPrescriptionPerformance = (props) => {
    const dispatch = useDispatch();
    const { setSelectedYear } = actions;
    const {
        selectedYear,
        years,
        performance,
        isMonthlyPrescriptionDataLoading,
    } = useSelector((state) => state.prescription);
    const { company, isOmaniChannel } = useSelector((state) => state.company);
    const { startDateRange, endDateRange } = useSelector(
        (state) => state.filter,
    );
    const [chartSeries, setChartSeries] = useState([]);
    const BsAbCompanies = [41, 51, 53, 59, 72, 90, 92];

    const handleTitle = () => {
        if (BsAbCompanies.includes(company.id)) {
            return 'Monthly BsAb class usage';
        }
        if (isOmaniChannel) {
            return 'Monthly Script Lift';
        } else return 'Monthly Prescription Performance';
    };
    const TITLE_MAPPING = {
        33: 'Total Bottles',
        62: 'Total Bottles',
        161: 'Total Bottles',
        45: 'Total Patients',
        58: 'Total Patients',
        102: 'Total Unique Prescribers',
        81: 'TRx',
        107: 'Total SD Accounts',
        default: 'Total Rx',
    };

    const getTitleByCompanyId = (companyId) =>
        TITLE_MAPPING[companyId] || TITLE_MAPPING.default;

    const handlePerformanceYear = (year) => {
        if (year !== selectedYear) {
            dispatch(setSelectedYear(year));
            dispatch(
                getPrescriptionPerformanceByYear({
                    companyId: company.id,
                    year: year,
                }),
            );
        }
    };
    const handleChartOptions = () => {
        const options = {
            ...CHART_OPTIONS,
            yaxis: {
                ...CHART_OPTIONS.yaxis,
                title: {
                    ...CHART_OPTIONS.yaxis.title,
                    text: getTitleByCompanyId(company.id),
                },
            },
            plotOptions: {
                ...CHART_OPTIONS.plotOptions,
                bar: {
                    ...CHART_OPTIONS.plotOptions.bar,
                    dataLabels: {
                        position: 'top',
                        enabled: [33, 62, 161].includes(company.id),
                        total: {
                            enabled: [33, 62, 161].includes(company.id),
                            offsetX: 0,
                            style: { fontSize: '10px' },
                            formatter: function (val, opts) {
                                const index = opts.dataPointIndex;
                                const data =
                                    performance?.totalPrescriptions?.[index]
                                        .sd_data || 0;
                                return 'SD - ' + data;
                            },
                        },
                    },
                },
            },
        };

        if (company.id === 42) {
            options.tooltip = {
                custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                    const dataPoint = series[seriesIndex][dataPointIndex];
                    const { totalPatients } =
                        w.config.series[seriesIndex].data[dataPointIndex];
                    const newPatients =
                        w.config.series[seriesIndex].data[dataPointIndex]
                            .newpatients;
                    let tooltip = '<div class="custom-tooltip">';
                    tooltip += `<div class="tooltip-header">${w.config.series[seriesIndex].name}</div>`;
                    tooltip += '<div class="tooltip-body">';
                    tooltip += `<div>Qty: ${dataPoint}</div>`;
                    if (totalPatients !== 0) {
                        tooltip += `<div>Total Patients: ${totalPatients}</div>`;
                    }
                    if (newPatients !== 0) {
                        tooltip += `<div>New Patients: ${newPatients}</div>`;
                    }

                    tooltip += '</div>'; // Close tooltip-body
                    tooltip += '</div>'; // Close custom-tooltip
                    return tooltip;
                },
            };
        }
        if (company.id === 45) {
            options.tooltip = {
                custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                    const currentDataPoint =
                        w.config.series[seriesIndex].data[dataPointIndex];
                    const attributed = currentDataPoint.y;
                    const { nonAttributed } = currentDataPoint;
                    return `
                      <div style="padding: 8px; font-size: 12px;">
                        <strong>Month: ${currentDataPoint.x}</strong><br />
                        <span>Attributed: ${attributed}</span><br />
                        <span>Non-Attributed: ${nonAttributed}</span>
                      </div>`;
                },
            };
        }
        if (company.id === 58) {
            options.tooltip = {
                custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                    const currentDataPoint =
                        w.config.series[seriesIndex].data[dataPointIndex];
                    const attributed = currentDataPoint.y;
                    const { nonAttributed } = currentDataPoint;
                    return `
                      <div style="padding: 8px; font-size: 12px;">
                        <strong>Month: ${currentDataPoint.x}</strong><br />
                        <span>Attributed: ${attributed}</span><br />
                        <span>Non-Attributed: ${nonAttributed}</span>
                      </div>`;
                },
            };
        }
        if (company.id === 81) {
            options.tooltip = {
                custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                    const currentDataPoint =
                        w.config.series[seriesIndex].data[dataPointIndex];
                    const attributed = currentDataPoint.y;
                    const { nonAttributed } = currentDataPoint;
                    return `
                      <div style="padding: 8px; font-size: 12px;">
                        <strong>Month: ${currentDataPoint.x}</strong><br />
                        <span>Attributed: ${attributed}</span><br />
                        <span>Non-Attributed: ${nonAttributed}</span>
                      </div>`;
                },
            };
        }

        return options;
    };

    const handleCustomisationForChart = () => {
        const companyId = [33, 38, 42, 40, 45, 54, 62, 161, 58, 81, 102, 107];
        return companyId.includes(company.id);
    };

    useEffect(() => {
        dispatch(actions.reset());
    }, [startDateRange, endDateRange, company.id]);
    useEffect(() => {
        const months = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
        ];

        if (
            performance &&
            performance.totalPrescriptions &&
            performance.totalPrescriptions.length > 0
        ) {
            let chartData;
            if (handleCustomisationForChart()) {
                switch (company.id) {
                    case 33:
                        chartData = [
                            {
                                name: 'New Bottles',
                                data: performance.totalPrescriptions.map(
                                    (month) => month.new_bottles,
                                ),
                            },
                            {
                                name: 'Refill Bottles',
                                data: performance.totalPrescriptions.map(
                                    (month) => month.refills,
                                ),
                            },
                        ];
                        break;
                    case 62:
                        chartData = [
                            {
                                name: 'New Bottles',
                                data: performance.totalPrescriptions.map(
                                    (month) => month.new_bottles,
                                ),
                            },
                            {
                                name: 'Refill Bottles',
                                data: performance.totalPrescriptions.map(
                                    (month) => month.refills,
                                ),
                            },
                        ];
                        break;
                    case 161:
                        chartData = [
                            {
                                name: 'New Bottles',
                                data: performance.totalPrescriptions.map(
                                    (month) => month.new_bottles,
                                ),
                            },
                            {
                                name: 'Refill Bottles',
                                data: performance.totalPrescriptions.map(
                                    (month) => month.refills,
                                ),
                            },
                        ];
                        break;
                    case 38:
                        chartData = [
                            {
                                name: 'SD',
                                data: performance.totalPrescriptions.map(
                                    (month) => month.sd_rx_data,
                                ),
                            },
                            {
                                name: 'SP',
                                data: performance.totalPrescriptions.map(
                                    (month) => month.sp_rx_data,
                                ),
                            },
                        ];
                        break;
                    case 42:
                        chartData = [
                            {
                                name: '1mg (SP)',
                                data: performance.totalPrescriptions.map(
                                    (item, index) => ({
                                        x: months[index],
                                        y: item.sp_1mg_rx_data,
                                        totalPatients:
                                            item.sp_1mg_total_unique_patients_data,
                                        newpatients:
                                            item.sp_1mg_total_new_patients_data,
                                    }),
                                ),
                                color: '#F3DBF1',
                            },
                            {
                                name: '5mg (SP)',
                                data: performance.totalPrescriptions.map(
                                    (item, index) => ({
                                        x: months[index],
                                        y: item.sp_5mg_rx_data,
                                        totalPatients:
                                            item.sp_5mg_total_unique_patients_data,
                                        newpatients:
                                            item.sp_5mg_total_new_patients_data,
                                    }),
                                ),
                                color: '#DB97D2',
                            },
                            {
                                name: '1mg (SD)',
                                data: performance.totalPrescriptions.map(
                                    (item, index) => ({
                                        x: months[index],
                                        y: item.sd_1mg_rx_data,
                                        totalPatients:
                                            item.sd_1mg_total_unique_patients_data,
                                        newpatients:
                                            item.sd_1mg_total_new_patients_data,
                                    }),
                                ),
                                color: '#AD509E',
                            },
                            {
                                name: '5mg (SD)',
                                data: performance.totalPrescriptions.map(
                                    (item, index) => ({
                                        x: months[index],
                                        y: item.sd_5mg_rx_data,
                                        totalPatients:
                                            item.sd_5mg_total_unique_patients_data,
                                        newpatients:
                                            item.sd_5mg_total_new_patients_data,
                                    }),
                                ),
                                color: '#733567',
                            },
                        ];
                        break;
                    case 40:
                        chartData = [
                            {
                                name: 'Total Rx',
                                data: performance.totalPrescriptions.map(
                                    (item) => item.total,
                                ),
                            },
                        ];
                        break;
                    case 45:
                        chartData = [
                            {
                                name: 'Attributed',
                                data: performance.totalPrescriptions.map(
                                    (item, index) => ({
                                        x: months[index],
                                        y: item.attributed_patients,
                                        nonAttributed:
                                            item.non_attributed_patients,
                                    }),
                                ),
                            },
                        ];
                        break;
                    case 58:
                        chartData = [
                            {
                                name: 'Attributed',
                                data: performance.totalPrescriptions.map(
                                    (item, index) => ({
                                        x: months[index],
                                        y: item.attributed_patients,
                                        nonAttributed:
                                            item.non_attributed_patients,
                                    }),
                                ),
                            },
                        ];
                        break;
                    case 81:
                        chartData = [
                            {
                                name: 'Attributed',
                                data: performance.totalPrescriptions.map(
                                    (item, index) => ({
                                        x: months[index],
                                        y: item.attributed_patients,
                                        nonAttributed:
                                            item.non_attributed_patients,
                                    }),
                                ),
                            },
                        ];
                        break;
                    case 54:
                        chartData = [
                            {
                                name: 'SD',
                                data: performance.totalPrescriptions.map(
                                    (month) => month.sd_rx_data,
                                ),
                            },
                            {
                                name: 'SP',
                                data: performance.totalPrescriptions.map(
                                    (month) => month.sp_rx_data,
                                ),
                            },
                        ];
                        break;
                    default:
                        chartData = [
                            {
                                name:
                                    company.id === 102
                                        ? 'Total Unique Prescribers'
                                        : company.id === 107
                                          ? 'Total SD Accounts'
                                          : 'Total Patients',
                                data: performance.totalPrescriptions.map(
                                    (item) => item.total,
                                ),
                            },
                        ];
                }
                setChartSeries(chartData);
            } else {
                chartData = [
                    {
                        name:
                            company.id === 102
                                ? 'Total Unique Prescribers'
                                : 'Total Patients',
                        data: performance.totalPrescriptions.map(
                            (item) => item.total,
                        ),
                    },
                ];
                setChartSeries(chartData);
            }
        }
    }, [company.id, performance]);
    return (
        <Box data-testid="dashboard_MPP">
            <Typography className="text-lg-semi-bold gray-900">
                {handleTitle()}
            </Typography>
            {isMonthlyPrescriptionDataLoading ? (
                <PerformanceSkeleton />
            ) : (
                <>
                    <Box className="margin-tb-20">
                        <CustomButtonGroup
                            data={years}
                            handleClick={(year) => handlePerformanceYear(year)}
                            key="monthly-prescription-performance"
                            selected={selectedYear}
                        />
                    </Box>
                    <Box>
                        <StackedBarChart
                            chartOptions={
                                handleCustomisationForChart()
                                    ? handleChartOptions()
                                    : CHART_OPTIONS
                            }
                            chartSeries={chartSeries}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default MonthlyPrescriptionPerformance;
